import { useQuery } from 'react-query'
import { request } from '../utils/api'
import { HTTPMethod } from '../utils/api.types'
import type { Response } from '../utils/api.types'
import { type LiveLabel, type LiveStatus } from './useWebinarsQuery'

interface GetLiveListRequest {
  live_series_id: number
  include_private_p?: boolean
  include_speakers_p?: boolean
  stream_preview_p?: boolean
  not_live_id?: string
  size?: number
  token?: string
}

interface GetLiveListResponse extends Response {
  data: WebinarEventDTO[]
}

interface WebinarEventDTO {
  live_id: number
  name: string
  live_status: LiveStatus
  link: string
  live_date_epoch: number
  live_series_switch_link: string
  duration_minutes: number | ''
  speakers?: WebinarSpeakerDTO[]
  thumbnail_standard_download: string
  live_label: LiveLabel
  primary_timezone: string
}

interface WebinarSpeakerDTO {
  live_speaker_id: number
  name: string
  email: string
  title: string
  company: string
  has_avatar_p: boolean
  hidden_p: boolean
  avatar_url: string
}

export interface WebinarEvent {
  liveId: number
  name: string
  liveStatus: LiveStatus
  startDate: Date
  endDate?: Date
  speakers: WebinarSpeaker[]
  thumbnail: string
  link: string
  linkSwitch: string
  liveLabel: LiveLabel
  live_date_epoch: number // TODO: since redux is dependant on specific properties, we need this here.
  primary_timezone: string // TODO: since redux is dependant on specific properties, we need this here.
  live_id: number // TODO: since redux is dependant on specific properties, we need this here.
}

export interface WebinarSpeaker {
  liveSpeakerId: number
  name: string
  email: string
  title: string
  company: string
  hasAvatarP: boolean
  hiddenP: boolean
  avatarUrl: string
}

const getLiveList = (data: GetLiveListRequest) =>
  request<GetLiveListRequest, GetLiveListResponse>({
    url: '/api/2/live/list',
    method: HTTPMethod.GET,
    data,
  })

interface UseEventSessionsQueryOpts {
  id: number
  refetchInterval?: number | false
  excludeSessionId?: string
  token?: string
  includePrivate?: boolean
  includeSpeakers?: boolean
  includeStreamPreview?: boolean
  enabled?: boolean
}

const useEventSessionsQuery = (options: UseEventSessionsQueryOpts) => {
  const {
    id,
    refetchInterval = false,
    includePrivate,
    includeSpeakers,
    includeStreamPreview,
    excludeSessionId,
    token,
    enabled = true,
  } = options

  return useQuery(
    ['digital-event-sessions', options],
    async () => {
      const response = await getLiveList({
        live_series_id: id,
        not_live_id: excludeSessionId,
        include_private_p: includePrivate,
        include_speakers_p: includeSpeakers,
        stream_preview_p: includeStreamPreview,
        size: 100,
        token,
      })

      const webinarEvents = response.data.map<WebinarEvent>(
        (webinarEventDTO) => {
          const webinarSpeakerDTOs = webinarEventDTO.speakers

          const webinarSpeakers = webinarSpeakerDTOs?.map(
            (webinarSpeakerDTO) => ({
              liveSpeakerId: webinarSpeakerDTO.live_speaker_id,
              name: webinarSpeakerDTO.name,
              email: webinarSpeakerDTO.email,
              title: webinarSpeakerDTO.title,
              company: webinarSpeakerDTO.company,
              hasAvatarP: webinarSpeakerDTO.has_avatar_p,
              hiddenP: webinarSpeakerDTO.hidden_p,
              avatarUrl: webinarSpeakerDTO.avatar_url,
            })
          )

          return {
            name: webinarEventDTO.name,
            liveStatus: webinarEventDTO.live_status,
            startDate: new Date(webinarEventDTO.live_date_epoch * 1000),
            endDate:
              webinarEventDTO.duration_minutes !== ''
                ? new Date(
                    webinarEventDTO.live_date_epoch * 1000 +
                      webinarEventDTO.duration_minutes * 60 * 1000
                  )
                : undefined,
            speakers: webinarSpeakers || [],
            thumbnail: webinarEventDTO.thumbnail_standard_download,
            link: webinarEventDTO.link,
            liveId: webinarEventDTO.live_id,
            linkSwitch: webinarEventDTO.live_series_switch_link,
            liveLabel: webinarEventDTO.live_label,
            live_date_epoch: webinarEventDTO.live_date_epoch,
            primary_timezone: webinarEventDTO.primary_timezone,
            live_id: webinarEventDTO.live_id,
          }
        }
      )

      return webinarEvents
    },
    {
      refetchInterval,
      enabled,
    }
  )
}

export default useEventSessionsQuery

// flag for telling if a session is considered a previous session that doesn't
// have on-demand.
export const isPreviousNotOnDemand = (session: WebinarEvent) =>
  session.liveStatus === 'previous' && session.liveLabel !== 'Ondemand'

// flag for telling if a session is considered a live session
export const isLive = (session: WebinarEvent) => session.liveStatus === 'live'
